import { QueryClientProvider } from '@tanstack/react-query';
import { ReactNode, Suspense } from 'react';
import { queryClient } from '@/lib/query-client';
import { lazyDevtool } from '@/config/devtools';
import { ModalStackProvider } from '@bliks-io/ui';

interface AppProviderProps {
  children: ReactNode;
}

const ReactQueryDevtools = lazyDevtool(() =>
  import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools }))
);

/**
 * V2 App Provider
 *
 * Wraps the v2 application with all necessary providers:
 * - QueryClientProvider: For server state management via TanStack Query
 * - ModalStackProvider: For proper modal stacking and visual hierarchy
 * - ReactQueryDevtools: query-debugging tools, gated by env.SHOW_DEVTOOLS via lazyDevtool
 *
 * Usage:
 * Wrap your v2 routes or pages with this provider:
 *
 * ```tsx
 * <AppProvider>
 *   <YourV2Component />
 * </AppProvider>
 * ```
 *
 * Or in router:
 * ```tsx
 * <Route path="/v2/*" element={
 *   <AppProvider>
 *     <V2Routes />
 *   </AppProvider>
 * } />
 * ```
 */
export function AppProvider({ children }: AppProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ModalStackProvider>{children}</ModalStackProvider>
      {ReactQueryDevtools && (
        <Suspense fallback={null}>
          {/* bottom-right: bottom-left would cover the setup wizard's
              bottom-pinned Überblick rail entry during local testing. */}
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-right"
            position="bottom"
          />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
